import { ReactNode } from 'react';
import { Box, Divider, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CalculateOutlined,
  CheckCircleRounded,
  LocalShippingOutlined,
  ReceiptLongOutlined,
  SellOutlined,
} from '@mui/icons-material';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { useAppTFunction } from '@retail/app/i18n';
import { ConditionStepType } from '../allConditionsSchema';

type PaletteKey = 'info' | 'warning' | 'success' | 'secondary';

const stepStyle: Record<ConditionStepType, { color: PaletteKey; icon: ReactNode }> = {
  purchase: { color: 'info', icon: <ReceiptLongOutlined /> },
  freight: { color: 'warning', icon: <LocalShippingOutlined /> },
  salesPrice: { color: 'success', icon: <SellOutlined /> },
  rounding: { color: 'secondary', icon: <CalculateOutlined /> },
};

interface Props {
  step: ConditionStepType;
  filled: boolean;
  validFrom?: string;
  value: ReactNode;
}

export function ConditionSummaryCard({ step, filled, validFrom, value }: Props) {
  const t = useCalculusTFunction();
  const appT = useAppTFunction();

  const { color, icon } = stepStyle[step];
  const title: Record<ConditionStepType, string> = {
    purchase: t('calculus.createAllConditions.steps.purchase'),
    freight: t('calculus.createAllConditions.steps.freight'),
    salesPrice: t('calculus.createAllConditions.steps.salesPrice'),
    rounding: t('calculus.createAllConditions.steps.rounding'),
  };

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        p: 2,
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        borderColor: filled
          ? theme.vars
            ? `rgba(${theme.vars.palette[color].mainChannel} / 0.4)`
            : alpha(theme.palette[color].main, 0.4)
          : 'divider',
        backgroundColor: filled
          ? theme.vars
            ? `rgba(${theme.vars.palette[color].mainChannel} / 0.05)`
            : alpha(theme.palette[color].main, 0.05)
          : 'transparent',
      })}
    >
      <Stack direction="row" alignItems="center" gap={1.5}>
        <Box
          sx={(theme) => ({
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: filled
              ? theme.vars
                ? `rgba(${theme.vars.palette[color].mainChannel} / 0.15)`
                : alpha(theme.palette[color].main, 0.15)
              : 'action.hover',
            color: filled ? theme.vars.palette[color].main : 'text.disabled',
          })}
        >
          {icon}
        </Box>
        <Typography variant="subtitle2" flex={1}>
          {title[step]}
        </Typography>
        {filled && <CheckCircleRounded fontSize="small" sx={{ color: `${color}.main` }} />}
      </Stack>

      <Divider />

      <Box sx={{ flex: 1 }}>
        {filled ? (
          value
        ) : (
          <Typography variant="body2" color="text.disabled">
            {t('calculus.createAllConditions.notFilled')}
          </Typography>
        )}
      </Box>

      {filled && (
        <Typography variant="body3" color="text.secondary">
          {appT('markup.activeFromTo')}: {validFrom} →
        </Typography>
      )}
    </Paper>
  );
}
